/**
 * DuplicateWarningPopover - Hover/click popover for duplicate order warnings.
 *
 * Shows a small orange/green icon. On hover, displays a popover with:
 *   - List of matching orders (clickable to open detail modal)
 *   - Approve/Revert button to toggle resolution status
 */
import React, { useState, useRef, useEffect } from 'react';
import { Copy, Check, AlertTriangle, ExternalLink } from 'lucide-react';
import OrderDetailModal from './OrderDetailModal';

const DuplicateWarningPopover = ({ order, onDecision, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [modalOrderId, setModalOrderId] = useState(null);
  const [updating, setUpdating] = useState(false);
  const timeoutRef = useRef(null);
  const popoverRef = useRef(null);
  const mounted = useRef(false);
  const pending = useRef(null);
  const observation = JSON.stringify([order.id, order.duplicate_warning]);
  const lifetime = useRef({ observation, generation: 0 });
  if (lifetime.current.observation !== observation) lifetime.current = { observation, generation: lifetime.current.generation + 1 };
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; clearTimeout(timeoutRef.current); };
  }, []);
  useEffect(() => { pending.current = null; setUpdating(false); setModalOrderId(null); }, [observation]);

  const dw = order.duplicate_warning;
  if (!dw) return null;

  const isApproved = dw.resolution === 'approved';

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 250);
  };

  const toggleResolution = async (e) => {
    e.stopPropagation();
    if (!mounted.current || pending.current || disabled || !onDecision) return;
    const generation = lifetime.current.generation;
    const attempt = {};
    pending.current = attempt;
    const current = () => mounted.current && lifetime.current.generation === generation;
    const newResolution = isApproved ? 'unaddressed' : 'approved';
    setUpdating(true);
    try {
      await onDecision(order, newResolution, current);
    } finally {
      if (current() && pending.current === attempt) { pending.current = null; setUpdating(false); }
    }
  };

  return (
    <div
      className="relative inline-flex"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Icon */}
      <span
        className={`inline-flex items-center justify-center w-5 h-5 rounded-full flex-shrink-0 cursor-pointer transition-colors ${
          isApproved
            ? 'bg-green-100 text-green-600'
            : 'bg-orange-100 text-orange-600'
        }`}
        data-testid={`duplicate-warning-badge-${order.id}`}
      >
        {isApproved ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      </span>

      {/* Popover */}
      {open && (
        <div
          ref={popoverRef}
          className="absolute z-50 top-full left-0 mt-1.5 w-72 bg-white rounded-xl shadow-lg border border-stone-200 overflow-hidden"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          data-testid={`duplicate-popover-${order.id}`}
        >
          {/* Header */}
          <div className={`px-3 py-2 text-xs font-semibold flex items-center gap-1.5 ${
            isApproved ? 'bg-green-50 text-green-800' : 'bg-orange-50 text-orange-800'
          }`}>
            {isApproved ? <Check className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
            {isApproved ? 'Duplicate — Approved' : 'Possible Duplicate'}
          </div>

          {/* Match list */}
          <div className="p-2 space-y-1">
            <p className="text-[11px] text-stone-500 px-1">Similar orders found:</p>
            {dw.matches.map((match) => (
              <button
                key={match.id}
                onClick={(e) => {
                  e.stopPropagation();
                  setModalOrderId(match.id);
                }}
                className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-stone-50 flex items-center justify-between group transition-colors"
                data-testid={`duplicate-match-${match.id}`}
              >
                <span className="text-sm font-medium text-stone-800">
                  #{match.internal_order_number || match.id.slice(0, 8)}
                </span>
                <ExternalLink className="h-3 w-3 text-stone-400 group-hover:text-primary transition-colors" />
              </button>
            ))}
          </div>

          {/* Approve / Revert button */}
          <div className="px-2 pb-2">
            <button
              onClick={toggleResolution}
              disabled={updating || disabled || !onDecision}
              className={`w-full py-1.5 rounded-lg text-xs font-medium transition-colors ${
                isApproved
                  ? 'bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200'
                  : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
              }`}
              data-testid={`duplicate-toggle-${order.id}`}
            >
              {updating ? 'Updating...' : isApproved ? 'Mark as Unaddressed' : 'Approve as Not Duplicate'}
            </button>
          </div>
        </div>
      )}

      {/* Detail modal for a clicked match */}
      {modalOrderId && (
        <OrderDetailModal
          orderId={modalOrderId}
          onClose={() => setModalOrderId(null)}
        />
      )}
    </div>
  );
};

export default DuplicateWarningPopover;
